import { IntakeFormQuestionItem } from "@/modules/agent/components/VendorAssessmentIntakeForm/IntakeFormQuestionItem";
import { intakeFormValidationSchema } from "@/modules/agent/components/VendorAssessmentIntakeForm/intake-form.schema";
import type { IntakeFormQuestion } from "@/modules/agent/requests/vendor-intake-form-response";
import { ThirdPartyVendorLayout } from "@/modules/vendors/third-party-evaluation-form";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import ZaniaLogoLoader from "@/shared/components/page-loader";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from "@nextui-org/react";
import Lottie from "lottie-react";
import { Ellipsis, Loader, Plus, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  answerBusinessOwnerQuestion,
  authenticateBusinessOwner,
  createBusinessOwnerForm,
  fetchBusinessOwnerFormExternal,
  submitBusinessOwnerForm,
} from "../../requests/intake-form-v2";
import { mapV2QuestionsToIntakeQuestions } from "../intake-review/utils/map-v2-to-intake-question";
import type { BusinessOwnerForm } from "../../types/intake-form-v2";
import SuccessAnimation from "../../../../shared/lottie/success.json";
import CreateFormModal from "./CreateFormModal";

type PageState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "create_form" }
  | { status: "form"; form: BusinessOwnerForm; questions: IntakeFormQuestion[] }
  | { status: "submitted"; vendorName: string; productName: string };

interface IntakeFormData {
  questions: IntakeFormQuestion[];
}

const BusinessOwnerAuthPage = () => {
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<PageState>({ status: "loading" });
  const tokenRef = useRef(token);

  // Auto-save state
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pendingAutoSaveCallsRef = useRef<Set<string>>(new Set());
  const debounceTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const formIdRef = useRef<string>("");

  // Form setup
  const formMethods = useForm<IntakeFormData>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(intakeFormValidationSchema) as any,
    defaultValues: { questions: [] },
  });

  const { control, watch, reset, setValue } = formMethods;
  const { fields } = useFieldArray({ control, name: "questions" });

  // Authenticate on mount
  useEffect(() => {
    if (!tokenRef.current) {
      setPageState({ status: "error", message: "Invalid authentication link." });
      return;
    }

    const authenticate = async () => {
      try {
        const resp = await authenticateBusinessOwner(tokenRef.current!);
        if (!resp.success) {
          setPageState({ status: "error", message: "Authentication failed." });
          return;
        }
        if (!resp.business_owner_form) {
          setPageState({ status: "create_form" });
          return;
        }
        await loadForm(resp.business_owner_form);
      } catch {
        setPageState({
          status: "error",
          message: "Authentication failed. The link may have expired.",
        });
      }
    };

    void authenticate();
  }, []);

  const loadForm = async (form: BusinessOwnerForm) => {
    if (form.intake_form_state === "submitted") {
      setPageState({ status: "submitted", vendorName: form.vendor_name, productName: form.product_name || "" });
      return;
    }
    try {
      const data = await fetchBusinessOwnerFormExternal(form.id);
      const questions = mapV2QuestionsToIntakeQuestions(data.questions)
        .filter((q) => !q.hidden_from_user);
      formIdRef.current = form.id;
      setPageState({ status: "form", form, questions });
    } catch {
      setPageState({ status: "error", message: "Failed to load the intake form." });
    }
  };

  // Initialize form fields when questions arrive
  useEffect(() => {
    if (pageState.status !== "form") return;
    reset({
      questions: pageState.questions.map((q) => {
        let initialResponse: string | string[];
        if (q.response !== null && q.response !== undefined) {
          initialResponse = q.response as string | string[];
        } else {
          initialResponse =
            q.question_type === "multiple_choice" ||
            q.question_type === "file_upload"
              ? []
              : "";
        }
        return { ...q, response: initialResponse, saveFailed: false };
      }),
    });
  }, [pageState, reset]);

  // Auto-save per question (same pattern as AssessorSideIntakeForm)
  const autoSaveQuestion = useCallback(
    async (question: IntakeFormQuestion, questionIndex: number) => {
      const callId = `${question.id}-${Date.now()}`;
      try {
        pendingAutoSaveCallsRef.current.add(callId);
        setIsSaving(true);

        let sourceUrls = question.source_urls || [];
        let response: string | string[] = question.response || "";

        if (question.question_type === "file_upload") {
          if (Array.isArray(question.response) && question.response.length > 0) {
            sourceUrls = question.response;
            response = "";
          }
        }

        await answerBusinessOwnerQuestion(
          formIdRef.current,
          question.id,
          { response, source_urls: sourceUrls },
        );

        setValue(`questions.${questionIndex}.saveFailed`, false);
      } catch (error) {
        console.error("Auto-save error:", error);
        setValue(`questions.${questionIndex}.saveFailed`, true);
      } finally {
        pendingAutoSaveCallsRef.current.delete(callId);
        if (pendingAutoSaveCallsRef.current.size === 0) {
          setIsSaving(false);
        }
      }
    },
    [setValue],
  );

  const debouncedAutoSave = useCallback(
    (question: IntakeFormQuestion, questionIndex: number) => {
      const questionId = question.id;
      const existingTimer = debounceTimersRef.current.get(questionId);
      if (existingTimer) clearTimeout(existingTimer);

      const timer = setTimeout(() => {
        void autoSaveQuestion(question, questionIndex);
        debounceTimersRef.current.delete(questionId);
      }, 1000);

      debounceTimersRef.current.set(questionId, timer);
    },
    [autoSaveQuestion],
  );

  // Track previous file responses for deletion detection
  const previousResponsesRef = useRef<Map<number, string[]>>(new Map());

  // Watch for changes and auto-save
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (
        name &&
        name.startsWith("questions.") &&
        name.endsWith(".response")
      ) {
        const match = name.match(/questions\.(\d+)\.response/);
        if (match) {
          const index = Number.parseInt(match[1], 10);
          const question = value.questions?.[index];
          if (question) {
            if (
              question.question_type === "file_upload" &&
              Array.isArray(question.response)
            ) {
              const previousResponse =
                previousResponsesRef.current.get(index) || [];
              const currentResponse = question.response.filter(
                (url): url is string => typeof url === "string",
              );
              previousResponsesRef.current.set(index, currentResponse);
              const isDeletion =
                currentResponse.length === 0 && previousResponse.length > 0;
              if (currentResponse.length === 0 && !isDeletion) return;
            }
            void debouncedAutoSave(question as IntakeFormQuestion, index);
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, debouncedAutoSave]);

  const handleCreateForm = async (
    vendorName: string,
    productName: string | null,
  ) => {
    try {
      const resp = await createBusinessOwnerForm({
        vendor_name: vendorName,
        product_name: productName,
      });
      const form = resp.business_owner_form;
      await loadForm(form);
    } catch {
      setPageState({ status: "error", message: "Failed to create the intake form." });
    }
  };

  const handleSubmit = async () => {
    if (pageState.status !== "form") return;
    const { form } = pageState;
    try {
      setIsSubmitting(true);
      await submitBusinessOwnerForm(formIdRef.current);
      setPageState({
        status: "submitted",
        vendorName: form.vendor_name,
        productName: form.product_name || "",
      });
    } catch {
      setPageState({
        status: "error",
        message: "Failed to submit the intake form.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscardAndStartNew = () => {
    setPageState({ status: "create_form" });
  };

  // Loading state
  if (pageState.status === "loading") {
    return <ZaniaLogoLoader />;
  }

  // Error state
  if (pageState.status === "error") {
    return (
      <ThirdPartyVendorLayout>
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center flex flex-col gap-3">
            <h2 className="text-xl font-semibold text-gray-800">
              Something went wrong
            </h2>
            <p className="text-sm text-gray-500">{pageState.message}</p>
          </div>
        </div>
      </ThirdPartyVendorLayout>
    );
  }

  // Create form modal
  if (pageState.status === "create_form") {
    return (
      <ThirdPartyVendorLayout>
        <div className="w-full h-full flex items-center justify-center">
          <CreateFormModal isOpen onSubmit={handleCreateForm} />
        </div>
      </ThirdPartyVendorLayout>
    );
  }

  // Submitted state
  if (pageState.status === "submitted") {
    return (
      <ThirdPartyVendorLayout>
        <div className="w-full h-full flex flex-col">
          {/* Breadcrumbs + Menu row */}
          <div className="flex items-center pb-4 min-h-[44px] max-h-[44px] justify-between">
            <ZBreadCrumbs
              items={[
                { title: "Third-Party Risk", href: "#" },
                { title: pageState.vendorName, href: "#" },
                { title: pageState.productName, href: "#" },
                { title: "Intake Form", href: "#" },
              ]}
            />
            <Dropdown>
              <DropdownTrigger>
                <Button isIconOnly className="bg-transparent">
                  <Ellipsis className="h-5 w-5" color="#717179" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                <DropdownItem
                  key="start-new"
                  onClick={handleDiscardAndStartNew}
                  startContent={
                    <Plus className="h-4 w-4 text-[#444444]" />
                  }
                  className="text-[#444444]"
                >
                  Start New
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
          {/* Success content */}
          <div className="w-full flex-1 bg-white flex items-center px-24 gap-32 rounded-lg">
            <div className="grow w-full flex flex-col items-start justify-center gap-4">
              <h4 className="text-4xl font-semibold leading-[60px]">
                Assessment Request Submitted for {pageState.vendorName} {pageState.productName}
              </h4>
              <h6 className="text-md text-[#71717A]">
                Your request has been sent to the Security Team for review. You will be notified once the assessment is complete or if further details are required.
              </h6>
            </div>
            <div className="grow w-full flex flex-col items-start justify-center gap-4">
              <Lottie animationData={SuccessAnimation} loop={false} />
            </div>
          </div>
        </div>
      </ThirdPartyVendorLayout>
    );
  }

  // Form view
  const { form } = pageState;

  return (
    <ThirdPartyVendorLayout>
      <div className="w-full h-full flex flex-col">
        {/* Breadcrumbs + Submit + Menu row */}
        <div className="flex items-center pb-4 min-h-[44px] max-h-[44px] justify-between">
          <ZBreadCrumbs
            items={[
              { title: "Third-Party Risk", href: "#" },
              { title: form.vendor_name, href: "#" },
              { title: form.product_name || "", href: "#" },
              { title: "Intake Form", href: "#" },
            ]}
          />
          <div className="flex items-center gap-2">
            {/* Auto-save indicator */}
            <div className="flex items-center h-7 gap-1">
              <Loader
                className={`w-4 h-4 animate-spin ${isSaving ? "" : "invisible"}`}
              />
              {isSaving && (
                <p className="text-tiny text-[#A5A5A5] text-[10px]">
                  Auto-saving
                </p>
              )}
            </div>
            {/* Submit button */}
            <Button
              color="primary"
              className="rounded-md text-sm leading-5 h-7"
              isLoading={isSubmitting}
              onClick={handleSubmit}
            >
              Submit
            </Button>
            {/* Three-dot menu */}
            <Dropdown>
              <DropdownTrigger>
                <Button isIconOnly className="bg-transparent">
                  <Ellipsis className="h-5 w-5" color="#717179" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                <DropdownItem
                  key="discard"
                  onClick={handleDiscardAndStartNew}
                  startContent={
                    <RotateCcw className="h-4 w-4 text-[#444444]" />
                  }
                  className="text-[#444444]"
                >
                  Discard &amp; Start New
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>

        {/* Form content */}
        <div className="w-full flex-1 bg-[#FAFAFA] bg-opacity-90 flex flex-col items-center overflow-auto">
          <div className="flex px-16 py-6 justify-between items-start border-b w-full bg-[#FEFEFE]">
            <div className="flex flex-col gap-2">
              <span className="font-semibold text-2xl">
                {form.vendor_name} {form.product_name || ""}
              </span>
              <span className="text-sm text-[#71717A]">
                Provide vendor context and describe its use, including relevant
                documents
              </span>
            </div>
          </div>
          <div className="px-16 py-4 flex-1 w-full overflow-y-auto">
            <div className="bg-[#FEFEFE] w-full rounded-lg p-6 flex flex-col gap-6">
              <FormProvider {...formMethods}>
                <form className="flex flex-col gap-6">
                  {fields.map((field, index) => (
                    <IntakeFormQuestionItem key={field.id} index={index} />
                  ))}
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </ThirdPartyVendorLayout>
  );
};

export default BusinessOwnerAuthPage;
